import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Popper } from "@mui/material";
import { CHART_TOOLTIP_GAP, CHART_TOOLTIP_HIDE_DELAY, CHART_TOOLTIP_TEST_ID } from "./HyroChartTooltip.consts";

// Tooltips for nivo charts; use Tooltip for DOM elements.
// Driven imperatively via ref: showing on hover must never re-render the owner chart —
// a nivo-layer re-render remounts the hovered <rect> and swallows its mouseleave.
const HyroChartTooltip = forwardRef(({
  renderContent,
  placement = "top",
  modifiers,
  gap = CHART_TOOLTIP_GAP,
  hideDelay = CHART_TOOLTIP_HIDE_DELAY
}, ref) => {
  const [tooltip, setTooltip] = useState(null);
  const shownRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const popperRef = useRef(null);
  const [resolvedPlacement, setResolvedPlacement] = useState(placement);
  const open = Boolean(tooltip);

  const cancelPendingHide = () => {
    clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = null;
  };

  const hideNow = () => {
    cancelPendingHide();
    shownRef.current = null;
    setTooltip(null);
  };

  useImperativeHandle(ref, () => ({
    show: (data, rect, options = {}) => {
      cancelPendingHide();
      const interactive = Boolean(options.interactive);
      if (interactive && shownRef.current?.data?.id === data?.id) return;
      shownRef.current = { data, rect, ...options, interactive };
      setTooltip(shownRef.current);
    },
    hide: () => {
      cancelPendingHide();
      if (!shownRef.current?.interactive) {
        hideNow();
        return;
      }
      hideTimeoutRef.current = setTimeout(hideNow, hideDelay);
    }
  }));

  useEffect(() => cancelPendingHide, []);

  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener("scroll", hideNow, { capture: true, passive: true });
    return () => window.removeEventListener("scroll", hideNow, { capture: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // a pinned tooltip has no mouseleave on touch devices, so a tap anywhere else dismisses it
  const interactive = Boolean(tooltip?.interactive);
  useEffect(() => {
    if (!interactive) return undefined;
    const hideOnOutsidePointer = (event) => {
      if (!popperRef.current?.contains(event.target)) hideNow();
    };
    document.addEventListener("pointerdown", hideOnOutsidePointer, true);
    return () => document.removeEventListener("pointerdown", hideOnOutsidePointer, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactive]);

  if (!tooltip) return null;

  const resolvedModifiers = typeof modifiers === "function" ? modifiers(tooltip) : modifiers;
  const trackPlacement = { name: "trackPlacement", enabled: true, phase: "write", fn: ({ state }) => setResolvedPlacement(state.placement) };
  const bridgeSide = resolvedPlacement.startsWith("bottom") ? "paddingTop" : "paddingBottom";

  return (
    <Popper
      open
      ref={popperRef}
      role="tooltip"
      data-testid={CHART_TOOLTIP_TEST_ID}
      placement={placement}
      anchorEl={{ getBoundingClientRect: () => tooltip.rect }}
      modifiers={[...(resolvedModifiers ?? [{ name: "offset", options: { offset: [0, interactive ? 0 : gap] } }]), trackPlacement]}
      sx={{
        pointerEvents: interactive ? "auto" : "none",
        [bridgeSide]: interactive ? `${gap}px` : 0,
        zIndex: "tooltip"
      }}
      onMouseEnter={cancelPendingHide}
      onMouseLeave={hideNow}
    >
      {renderContent(tooltip.data)}
    </Popper>
  );
});

HyroChartTooltip.displayName = "HyroChartTooltip";

export default HyroChartTooltip;
